// src/app/features/dashboard/widgets/ScarletWell/CheckIn/checkInComponents.js
import React from 'react'
import { Box, Button, Card, Typography, Divider } from '@material-ui/core'
import { getCategoryLabel, getResponseLabel } from './checkInHelpers'
import { trackWidgetClick } from '../../../../../helpers/trackWidgetClick'

// TODO: Extract this into firebase
const INTRO_COPY =
  'This inventory gives you a way of thinking about what you are doing, and how you are feeling in general. When you read each item, think about the things you have been doing for your personal wellness during the past 30 days.'

const SCALE_COPY =
  'Answer each item in a way that best captures how often the statement is true for you.'

export function SurveyIntroCard({ answers, classes }) {
  return (
    <Card className={classes.introCard} elevation={0}>
      <Box className={classes.introHeader}>
        <Typography variant="h2" className={classes.introHeaderText}>
          Wellness Inventory
        </Typography>
      </Box>
      <Box className={classes.introContent}>
        <Typography variant="h6" className={classes.introParagraph}>
          {INTRO_COPY}
        </Typography>
        <Typography variant="subtitle1" className={classes.introSubText}>
          {SCALE_COPY}
        </Typography>
        <Typography variant="h6" className={classes.introScaleText}>
          {answers}
        </Typography>
        <Divider className={classes.introDivider} />
      </Box>
    </Card>
  )
}

export function SurveyCategoryPanel({
  categories,
  activeCategoryId,
  invalidCategoryIds,
  hasAttemptedSubmit,
  isRecentSubmission,
  setSelectedCategoryId,
  dispatch,
  classes,
}) {
  return (
    <Box className={classes.categoryPanel}>
      {isRecentSubmission && (
        <Box className={classes.categoryWarningBanner} role="alert">
          <Typography variant="body1" className={classes.categoryWarningText}>
            Please wait 24 hours before submitting another Wellness Inventory
            survey
          </Typography>
        </Box>
      )}
      {!isRecentSubmission &&
        hasAttemptedSubmit &&
        invalidCategoryIds.length > 0 && (
          <Box className={classes.categoryWarningBanner} role="alert">
            <Typography variant="body1" className={classes.categoryWarningText}>
              Categories highlighted in red need to be completed before
              submission
            </Typography>
          </Box>
        )}
      <Box className={classes.categoryGridWrapper}>
        <Box className={classes.categoryGrid}>
          {categories.map((category) => {
            const isActive = category.categoryId === activeCategoryId
            const isInvalid =
              hasAttemptedSubmit &&
              invalidCategoryIds.includes(category.categoryId)

            return (
              <Button
                key={category.categoryId}
                className={`${classes.categoryButton} ${
                  isActive ? classes.activeCategoryButton : ''
                } ${isInvalid ? classes.invalidCategoryButton : ''}`}
                onClick={() => {
                  trackWidgetClick(
                    dispatch,
                    'ScarletWellCheckIn',
                    getCategoryLabel(category),
                  )
                  setSelectedCategoryId(category.categoryId)
                }}
                aria-invalid={isInvalid ? 'true' : undefined}
              >
                <Typography
                  variant="h6"
                  className={`${classes.categoryButtonText} ${
                    isActive ? classes.activeCategoryButtonText : ''
                  }`}
                >
                  {getCategoryLabel(category)}
                </Typography>
              </Button>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export function SurveyQuestionTable({
  activeCategory,
  responseOptions,
  selectedAnswers,
  setSelectedAnswers,
  answerScaleLayoutStyle,
  classes,
}) {
  return (
    <Box className={classes.questionTable}>
      <Box className={classes.questionTableHeader}>
        <Typography variant="h4" className={classes.tableHeaderLabel}>
          Questions
        </Typography>
        <Typography
          variant="h4"
          className={`${classes.tableHeaderLabel} ${classes.tableHeaderLabelRight}`}
        >
          Answers
        </Typography>
      </Box>

      <Box className={classes.answerScaleHeader}>
        <Box />
        <Box
          className={classes.answerScaleLabels}
          style={answerScaleLayoutStyle}
        >
          {responseOptions.map((responseOption) => (
            <Typography
              variant="body1"
              key={responseOption.responseId}
              className={classes.answerScaleLabel}
            >
              {getResponseLabel(responseOption)}
            </Typography>
          ))}
        </Box>
      </Box>

      {activeCategory && activeCategory.questions.length > 0 ? (
        activeCategory.questions.map((question, index) => (
          <Box
            key={question.questionId}
            className={`${classes.questionRow} ${
              index % 2 === 1 ? classes.questionRowAlternate : ''
            }`}
            role="radiogroup"
            aria-labelledby={`question-label-${question.questionId}`}
          >
            <Typography
              id={`question-label-${question.questionId}`}
              className={classes.questionText}
              variant="body1"
            >
              {question.questionText}
            </Typography>
            <Box className={classes.answerScale} style={answerScaleLayoutStyle}>
              {responseOptions.map((responseOption) => {
                const isChecked =
                  selectedAnswers[question.questionId] ===
                  responseOption.responseId

                return (
                  <label
                    key={responseOption.responseId}
                    className={`${classes.answerCell} ${
                      isChecked ? classes.answerCellActive : ''
                    }`}
                  >
                    <input
                      aria-label={`${question.questionText} ${responseOption.responseTitle}`}
                      checked={isChecked}
                      className={classes.hiddenInput}
                      name={`question-${question.questionId}`}
                      onChange={() =>
                        setSelectedAnswers((currentAnswers) => ({
                          ...currentAnswers,
                          [question.questionId]: responseOption.responseId,
                        }))
                      }
                      type="radio"
                      value={responseOption.responseId}
                    />
                    <span
                      className={`${classes.radioOuter} ${
                        isChecked ? classes.radioOuterChecked : ''
                      }`}
                    >
                      <span
                        className={`${classes.radioInner} ${
                          isChecked ? classes.radioInnerChecked : ''
                        }`}
                      />
                    </span>
                    <Typography
                      component="span"
                      variant="caption"
                      className={classes.mobileOptionLabel}
                    >
                      {getResponseLabel(responseOption)}
                    </Typography>
                  </label>
                )
              })}
            </Box>
          </Box>
        ))
      ) : (
        <Box className={classes.emptyState}>
          <Typography variant="body1">
            No questions are available for this category yet.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export function SurveyFooterActions({
  showPreviousButton,
  showNextButton,
  isSubmitting,
  isRecentSubmission,
  onPreviousCategory,
  onNextCategory,
  onSubmit,
  dispatch,
  classes,
}) {
  return (
    <Box className={classes.mobileFooterActions}>
      <Box className={classes.mobileFooterSide}>
        {showPreviousButton ? (
          <Button
            className={classes.mobileNavButton}
            onClick={(event) => {
              trackWidgetClick(
                dispatch,
                'ScarletWellCheckIn',
                'Previous Category',
              )
              onPreviousCategory(event)
            }}
          >
            <Typography variant="h6" className={classes.buttonText}>
              Previous
            </Typography>
          </Button>
        ) : null}
      </Box>
      <Box className={classes.mobileFooterSideRight}>
        {showNextButton ? (
          <Button
            className={classes.mobileNavButton}
            onClick={(event) => {
              trackWidgetClick(dispatch, 'ScarletWellCheckIn', 'Next Category')
              onNextCategory(event)
            }}
          >
            <Typography variant="h6" className={classes.buttonText}>
              Next
            </Typography>
          </Button>
        ) : null}
        <Button
          className={
            isSubmitting || isRecentSubmission
              ? classes.mobileSubmitButtonDisabled
              : classes.mobileSubmitButton
          }
          onClick={
            isSubmitting || isRecentSubmission
              ? undefined
              : () => {
                  trackWidgetClick(
                    dispatch,
                    'ScarletWellCheckIn',
                    'Submit Survey',
                  )
                  onSubmit()
                }
          }
          disabled={isSubmitting || isRecentSubmission}
          aria-disabled={isSubmitting || isRecentSubmission ? 'true' : 'false'}
        >
          <Typography
            variant="h6"
            className={
              isSubmitting || isRecentSubmission
                ? classes.buttonTextDisabled
                : classes.buttonText
            }
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Typography>
        </Button>
      </Box>
    </Box>
  )
}
